import Help from "@mui/icons-material/Help";
import { FormControl, FormHelperText, Input, InputAdornment, InputLabel, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import numeral from "numeral";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { emptyValues } from "../../../app/js/utils/constants";
import utils from "../../../app/js/utils/index";

// Styled components
const StyledFormControl = styled(FormControl)(() => ({
    marginBottom: "22px",
}));

const StyledInput = styled(Input)(({ theme }) => ({
    fontSize: 14,
    padding: "4px 6px",
    marginTop: "var(--bsinput-input-mt, 22px)",
    transition: theme.transitions.create(["border-color", "box-shadow"]),
}));

const StyledFormHelperText = styled(FormHelperText)(({ required }) => ({
    fontStyle: "italic",
    margin: "0px",

    ...(required && {
        color: "dimgrey",
    }),
}));

const StyledToolTipWrapper = styled("span")({
    lineHeight: "1.5",
});

const StyledCheckbox = styled("input")({
    marginLeft: "8px",
});

/** type = "number" should not be used. The built in browser behavior for an input element
 of this type includes preprocessing the value before validation with validation.js can be done.
 */
const BSInput = (props) => {
    const {
        checked,
        checkbox,
        checkboxVal,
        disableCheckbox,
        className,
        disabled,
        endAdornment,
        error,
        errorText,
        extraFormControlStyles,
        extraInputStyles,
        formcontrolClass,
        helperText,
        helperTextRequired,
        id,
        inputProps,
        label,
        multiline,
        noFullWidth,
        onClick,
        onChangeCheckbox,
        onFocus,
        onKeyPress,
        placeholder,
        rows,
        startAdornment,
        startAdornmentStyles,
        toolTipText,
        type,
        value,
        onChange,
        numeric,
        integer,
        phone,
    } = props;

    useEffect(() => {
        handleOnBlur();
    }, []);

    const handleChange = (e) => {
        if (emptyValues.has(e.target.value)) {
            const dummyEvent = { target: { id: id, value: null } };
            onChange(dummyEvent);
            return;
        }
        onChange(e);
    };

    const handleOnBlur = () => {
        if (emptyValues.has(value) || !onChange) return;

        const dummyEvent = {
            target: {
                id: id,
                value: formatValue(value),
            },
        };

        onChange(dummyEvent);
    };

    const formatNumber = (value, isInteger = false) => {
        return isInteger ? numeral(value).format("0,000") : numeral(value).format("0,000.00");
    };

    const formatValue = (value) => {
        if (emptyValues.has(value)) return null;

        // Date
        if (type === "date") return utils.operations.formatDate("YYYY-MM-DD")(value);
        // Integer or Numeric
        if (integer || numeric) return formatNumber(value, integer);
        // Phone Number
        if (phone) return utils.operations.formatPhoneNumber(value);

        return value;
    };

    const FormControlStyle = formcontrolClass || StyledFormControl;

    return (
        <FormControlStyle fullWidth={!noFullWidth} style={extraFormControlStyles}>
            {label && (
                <InputLabel shrink htmlFor={id} sx={{ overflow: "visible" }}>
                    {label}
                    {checkbox && (
                        <StyledCheckbox
                            type="checkbox"
                            disabled={disableCheckbox}
                            onChange={onChangeCheckbox}
                            checked={checked}
                            value={checkboxVal}
                        />
                    )}
                    {toolTipText && (
                        <StyledToolTipWrapper>
                            <Tooltip title={toolTipText} placement="right-start">
                                <span data-tooltip-id={`${id}-tool-tip`}>
                                    <Help style={{ fontSize: "110%" }} />
                                </span>
                            </Tooltip>
                        </StyledToolTipWrapper>
                    )}
                </InputLabel>
            )}
            <StyledInput
                id={id}
                value={type === "date" ? utils.operations.formatDate("YYYY-MM-DD")(value) : value}
                type={type}
                placeholder={placeholder}
                onChange={handleChange}
                disableUnderline={true}
                autoComplete={"no"}
                style={error ? { borderColor: "red", ...extraInputStyles } : extraInputStyles}
                className={className}
                endAdornment={endAdornment && <InputAdornment position="end">{endAdornment}</InputAdornment>}
                startAdornment={
                    startAdornment && (
                        <InputAdornment position="start" style={startAdornmentStyles}>
                            {startAdornment}
                        </InputAdornment>
                    )
                }
                disabled={disabled}
                multiline={multiline}
                rows={rows}
                onKeyPress={onKeyPress}
                onFocus={onFocus}
                onBlur={handleOnBlur}
                onClick={onClick}
                inputProps={inputProps}
            />
            {
                errorText && error && (
                    <StyledFormHelperText error={true}>{errorText}</StyledFormHelperText>
                ) /* Error Text */
            }
            {
                helperText && !error && (
                    <StyledFormHelperText required={helperTextRequired}>{helperText}</StyledFormHelperText>
                ) /* Helper Text */
            }
        </FormControlStyle>
    );
};

BSInput.propTypes = {
    className: PropTypes.string,
    disabled: PropTypes.bool,
    endAdornment: PropTypes.node,
    error: PropTypes.bool,
    errorText: PropTypes.string,
    extraFormControlStyles: PropTypes.object,
    extraInputStyles: PropTypes.object,
    formcontrolClass: PropTypes.string,
    helperText: PropTypes.string,
    helperTextRequired: PropTypes.bool,
    id: PropTypes.string.isRequired,
    inputProps: PropTypes.object,
    label: PropTypes.string,
    multiline: PropTypes.bool,
    noFullWidth: PropTypes.bool,
    onClick: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyPress: PropTypes.func,
    placeholder: PropTypes.string,
    rows: PropTypes.number,
    startAdornment: PropTypes.node,
    startAdornmentStyles: PropTypes.object,
    toolTipText: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.any,
    onChange: PropTypes.func,
    numeric: PropTypes.bool,
    integer: PropTypes.bool,
    phone: PropTypes.bool,
    checkbox: PropTypes.bool,
    checkboxVal: PropTypes.any,
    checked: PropTypes.bool,
    disableCheckbox: PropTypes.bool,
    onChangeCheckbox: PropTypes.func,
};

export default BSInput;
